import React, { useMemo, useState } from "react";
import {
    Box,
    Container,
    Paper,
    Stack,
    Typography,
    TextField,
    Button,
    IconButton,
    Chip,
} from "@mui/material";
import SectionTitle from "../components/SectionTitle";
import {
    FaWhatsapp,
    FaInstagram,
    FaEnvelope,
    FaPhone,
    FaArrowUpRightFromSquare,
} from "react-icons/fa6";

const WHATSAPP_PHONE = "5599999999999";
const INSTAGRAM_URL = "https://instagram.com/lscprodutora";
const EMAIL = "luscoprodutora@gmail.com";
const PHONE_DISPLAY = "(31) 99999-9999";

function buildWhatsAppLink(message) {
    const text = encodeURIComponent(message || "");
    return `https://wa.me/${WHATSAPP_PHONE}?text=${text}`;
}

const inputSx = {
    "& .MuiInputLabel-root": {
        color: "rgba(255,255,255,0.54)",
        fontWeight: 500,
    },
    "& .MuiInputLabel-root.Mui-focused": {
        color: "var(--verdeClaro)",
    },
    "& .MuiOutlinedInput-root": {
        color: "var(--branco)",
        borderRadius: "18px",
        background:
            "linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.025) 100%)",
        backdropFilter: "blur(10px)",
        transition: "all 220ms ease",
        "& fieldset": {
            borderColor: "rgba(255,255,255,0.10)",
        },
        "&:hover fieldset": {
            borderColor: "rgba(172,201,14,0.28)",
        },
        "&.Mui-focused": {
            boxShadow: "0 0 0 4px rgba(172,201,14,0.08)",
        },
        "&.Mui-focused fieldset": {
            borderColor: "rgba(172,201,14,0.55)",
        },
    },
};

function InfoMiniCard({ icon, label, value, href }) {
    return (
        <Box
            component="a"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
                textDecoration: "none",
                display: "block",
            }}
        >
            <Stack
                direction="row"
                spacing={1.6}
                alignItems="center"
                sx={{
                    p: 2,
                    borderRadius: "20px",
                    border: "1px solid rgba(255,255,255,0.10)",
                    background:
                        "linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.025) 100%)",
                    backdropFilter: "blur(10px)",
                    minHeight: 96,
                    transition: "all 220ms ease",
                    "&:hover": {
                        borderColor: "rgba(172,201,14,0.22)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 14px 40px rgba(0,0,0,0.18)",
                    },
                }}
            >
                <Box
                    sx={{
                        width: 52,
                        height: 52,
                        flexShrink: 0,
                        borderRadius: "16px",
                        display: "grid",
                        placeItems: "center",
                        color: "var(--verdeClaro)",
                        background:
                            "radial-gradient(circle at 30% 30%, rgba(172,201,14,0.22), rgba(172,201,14,0.08))",
                        border: "1px solid rgba(172,201,14,0.18)",
                    }}
                >
                    {icon}
                </Box>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                        sx={{
                            color: "rgba(255,255,255,0.52)",
                            fontSize: 11,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            mb: 0.35,
                        }}
                    >
                        {label}
                    </Typography>

                    <Typography
                        sx={{
                            color: "var(--branco)",
                            fontWeight: 800,
                            fontSize: "1rem",
                            lineHeight: 1.25,
                            wordBreak: "break-word",
                        }}
                    >
                        {value}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        color: "rgba(255,255,255,0.38)",
                        display: "grid",
                        placeItems: "center",
                    }}
                >
                    <FaArrowUpRightFromSquare size={12} />
                </Box>
            </Stack>
        </Box>
    );
}

export default function Contato() {
    const [form, setForm] = useState({
        nome: "",
        email: "",
        mensagem: "",
    });

    const quickMessage = useMemo(() => {
        const base = "Olá! Quero um orçamento com a LSC Produtora.";
        const parts = [];
        if (form.nome.trim()) parts.push(`Nome: ${form.nome.trim()}`);
        if (form.email.trim()) parts.push(`Email: ${form.email.trim()}`);
        if (form.mensagem.trim()) parts.push(`Mensagem: ${form.mensagem.trim()}`);
        return [base, ...parts].join("\n");
    }, [form]);

    function handleChange(e) {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    }

    function handleOpenWhatsApp() {
        window.open(buildWhatsAppLink(quickMessage), "_blank", "noopener,noreferrer");
    }

    return (
        <Box
            id="contato"
            sx={{
                position: "relative",
                py: { xs: 8, sm: 9, md: 10, lg: 11 },
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    top: 180,
                    left: "80%",
                    width: 280,
                    height: 280,
                    borderRadius: "50%",
                    background: "rgba(172,201,14,0.10)",
                    filter: "blur(90px)",
                    pointerEvents: "none",
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    bottom: -160,
                    right: "8%",
                    width: 340,
                    height: 340,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.05)",
                    filter: "blur(110px)",
                    pointerEvents: "none",
                }}
            />

            <Container
                maxWidth={false}
                sx={{
                    position: "relative",
                    zIndex: 1,
                    maxWidth: 1600,
                    mx: "auto",
                    px: { xs: 2, md: 3 },
                }}
            >
                <Box
                    sx={{
                        maxWidth: { xs: "100%", md: 980 },
                        width: "100%",
                    }}
                >
                    <SectionTitle
                        eyebrow="contato"
                        title="Vamos transformar sua próxima ideia em algo impossível de ignorar"
                        subtitle="Da direção criativa à entrega final, a Lusco constrói imagens, campanhas e narrativas com valor de marca."
                    />
                </Box>

                <Box
                    sx={{
                        mt: { xs: 3, md: 4 },
                        display: "grid",
                        gap: { xs: 2, md: 3 },
                        gridTemplateColumns: {
                            xs: "1fr",
                            lg: "minmax(420px, 0.92fr) minmax(620px, 1.28fr)",
                        },
                        alignItems: "stretch",
                    }}
                >
                    {/* ESQUERDA */}
                    <Paper
                        elevation={0}
                        sx={{
                            minHeight: { xs: "auto", lg: 760 },
                            p: { xs: 2.5, md: 4 },
                            borderRadius: "32px",
                            border: "1px solid rgba(255,255,255,0.10)",
                            background: `
                radial-gradient(circle at top left, rgba(172,201,14,0.12), transparent 32%),
                linear-gradient(180deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.03) 100%)
              `,
                            backdropFilter: "blur(18px)",
                            boxShadow:
                                "0 20px 80px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.04)",
                        }}
                    >
                        <Stack spacing={3} sx={{ height: "100%" }}>
                            <Stack spacing={1.5}>
                                <Chip
                                    label="Atendimento estratégico"
                                    sx={{
                                        alignSelf: "flex-start",
                                        height: 32,
                                        color: "var(--verdeClaro)",
                                        fontWeight: 800,
                                        letterSpacing: "0.04em",
                                        border: "1px solid rgba(172,201,14,0.20)",
                                        background: "rgba(172,201,14,0.08)",
                                    }}
                                />

                                <Typography
                                    sx={{
                                        color: "var(--branco)",
                                        fontWeight: 1000,
                                        fontSize: { xs: "2rem", md: "3.4rem" },
                                        lineHeight: 0.95,
                                        maxWidth: 560,
                                    }}
                                >
                                    Seu projeto merece uma abordagem mais ambiciosa.
                                </Typography>

                                <Typography
                                    sx={{
                                        color: "rgba(255,255,255,0.72)",
                                        fontSize: { xs: "1rem", md: "1.06rem" },
                                        lineHeight: 1.9,
                                        maxWidth: 560,
                                    }}
                                >
                                    Fale com a Lusco para alinhar orçamento, prazo, direção criativa,
                                    linguagem visual e objetivos do projeto. Atendimento direto,
                                    rápido e com visão estratégica de marca.
                                </Typography>
                            </Stack>

                            <Button
                                onClick={handleOpenWhatsApp}
                                startIcon={<FaWhatsapp />}
                                variant="contained"
                                size="large"
                                sx={{
                                    alignSelf: "flex-start",
                                    minHeight: 60,
                                    px: 4,
                                    borderRadius: 999,
                                    bgcolor: "var(--verdeClaro)",
                                    color: "var(--preto)",
                                    fontWeight: 1000,
                                    fontSize: "1rem",
                                    boxShadow: "0 18px 36px rgba(172,201,14,0.18)",
                                    "&:hover": {
                                        bgcolor: "var(--verdeClaro)",
                                        transform: "translateY(-1px)",
                                        boxShadow: "0 22px 42px rgba(172,201,14,0.22)",
                                    },
                                }}
                            >
                                Iniciar conversa no WhatsApp
                            </Button>

                            <Box
                                sx={{
                                    display: "grid",
                                    gap: 1.5,
                                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr" },
                                }}
                            >
                                <InfoMiniCard
                                    icon={<FaPhone size={16} />}
                                    label="Telefone"
                                    value={PHONE_DISPLAY}
                                    href={`https://wa.me/${WHATSAPP_PHONE}`}
                                />

                                <InfoMiniCard
                                    icon={<FaEnvelope size={16} />}
                                    label="E-mail"
                                    value={EMAIL}
                                    href={`mailto:${EMAIL}`}
                                />
                            </Box>

                            <Stack direction="row" spacing={1} sx={{ mt: "auto", pt: 1 }}>
                                <IconButton
                                    onClick={() =>
                                        window.open(
                                            buildWhatsAppLink("Olá! Quero falar com a LSC."),
                                            "_blank",
                                            "noopener,noreferrer"
                                        )
                                    }
                                    sx={{
                                        width: 50,
                                        height: 50,
                                        borderRadius: "16px",
                                        color: "var(--branco)",
                                        border: "1px solid rgba(255,255,255,0.10)",
                                        background: "rgba(255,255,255,0.04)",
                                        "&:hover": {
                                            borderColor: "rgba(172,201,14,0.28)",
                                            background: "rgba(172,201,14,0.08)",
                                        },
                                    }}
                                >
                                    <FaWhatsapp size={18} />
                                </IconButton>

                                <IconButton
                                    onClick={() =>
                                        window.open(INSTAGRAM_URL, "_blank", "noopener,noreferrer")
                                    }
                                    sx={{
                                        width: 50,
                                        height: 50,
                                        borderRadius: "16px",
                                        color: "var(--branco)",
                                        border: "1px solid rgba(255,255,255,0.10)",
                                        background: "rgba(255,255,255,0.04)",
                                        "&:hover": {
                                            borderColor: "rgba(172,201,14,0.28)",
                                            background: "rgba(172,201,14,0.08)",
                                        },
                                    }}
                                >
                                    <FaInstagram size={18} />
                                </IconButton>
                            </Stack>
                        </Stack>
                    </Paper>

                    {/* DIREITA */}
                    <Paper
                        elevation={0}
                        sx={{
                            minHeight: { xs: "auto", lg: 760 },
                            p: { xs: 2.5, md: 4 },
                            borderRadius: "32px",
                            border: "1px solid rgba(255,255,255,0.10)",
                            background:
                                "linear-gradient(180deg, rgba(255,255,255,0.065) 0%, rgba(255,255,255,0.03) 100%)",
                            backdropFilter: "blur(18px)",
                            boxShadow:
                                "0 20px 80px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.04)",
                        }}
                    >
                        <Stack spacing={3} sx={{ height: "100%" }}>
                            <Box>
                                <Typography
                                    sx={{
                                        color: "var(--branco)",
                                        fontWeight: 1000,
                                        fontSize: { xs: "1.5rem", md: "2.2rem" },
                                        mb: 1,
                                    }}
                                >
                                    Envie seu briefing
                                </Typography>

                                <Typography
                                    sx={{
                                        color: "rgba(255,255,255,0.66)",
                                        lineHeight: 1.85,
                                        fontSize: { xs: "0.96rem", md: "1rem" },
                                        maxWidth: 720,
                                    }}
                                >
                                    Preencha os pontos principais do projeto e abrimos sua mensagem
                                    pronta no WhatsApp para acelerar o atendimento com contexto,
                                    objetivo e direcionamento inicial.
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    display: "grid",
                                    gap: 2,
                                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                                }}
                            >
                                <TextField
                                    name="nome"
                                    value={form.nome}
                                    onChange={handleChange}
                                    fullWidth
                                    label="Seu nome"
                                    variant="outlined"
                                    sx={inputSx}
                                />

                                <TextField
                                    name="email"
                                    value={form.email}
                                    onChange={handleChange}
                                    fullWidth
                                    label="Seu e-mail"
                                    variant="outlined"
                                    sx={inputSx}
                                />
                            </Box>

                            <TextField
                                name="mensagem"
                                value={form.mensagem}
                                onChange={handleChange}
                                fullWidth
                                label="Conte um pouco sobre o projeto"
                                variant="outlined"
                                multiline
                                minRows={14}
                                sx={inputSx}
                            />

                            <Box
                                sx={{
                                    p: 2.2,
                                    borderRadius: "20px",
                                    border: "1px solid rgba(255,255,255,0.08)",
                                    background: "rgba(255,255,255,0.03)",
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: "rgba(255,255,255,0.46)",
                                        fontSize: 12,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.08em",
                                        mb: 0.7,
                                    }}
                                >
                                    fluxo de envio
                                </Typography>

                                <Typography
                                    sx={{
                                        color: "rgba(255,255,255,0.70)",
                                        fontSize: 14,
                                        lineHeight: 1.75,
                                    }}
                                >
                                    Ao clicar no botão abaixo, o WhatsApp será aberto com sua
                                    mensagem já estruturada para orçamento, atendimento ou briefing
                                    inicial.
                                </Typography>
                            </Box>

                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={1.6}
                                alignItems={{ sm: "center" }}
                                justifyContent="space-between"
                                sx={{ mt: "auto" }}
                            >
                                <Button
                                    onClick={handleOpenWhatsApp}
                                    startIcon={<FaWhatsapp />}
                                    variant="contained"
                                    size="large"
                                    sx={{
                                        minHeight: 60,
                                        px: 4,
                                        borderRadius: 999,
                                        bgcolor: "var(--verdeClaro)",
                                        color: "var(--preto)",
                                        fontWeight: 1000,
                                        boxShadow: "0 18px 36px rgba(172,201,14,0.18)",
                                        "&:hover": {
                                            bgcolor: "var(--verdeClaro)",
                                            transform: "translateY(-1px)",
                                            boxShadow: "0 22px 42px rgba(172,201,14,0.22)",
                                        },
                                    }}
                                >
                                    Enviar briefing no WhatsApp
                                </Button>

                                <Typography
                                    sx={{
                                        color: "rgba(255,255,255,0.48)",
                                        fontSize: 12,
                                        textAlign: { xs: "left", sm: "right" },
                                        maxWidth: 320,
                                    }}
                                >
                                    Resposta rápida para orçamentos, produção, direção criativa e campanhas.
                                </Typography>
                            </Stack>
                        </Stack>
                    </Paper>
                </Box>
            </Container>
        </Box>
    );
}